import React from 'react';
import { ReactComponent as Datepicker } from 'assets/icons/calendar.svg';
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form';
import { twJoin } from 'tailwind-merge';
import DatePicker, { DatePickerProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../react-datapicker/index.css';
import FieldLabel from './FieldLabel';
import FieldError from './FieldError';
import FieldDescribe from './FieldDescribe';

type ControlledRangeDatepickerProps<TFormValues extends FieldValues> = {
  name: Path<TFormValues>;
  label: string | React.ReactNode;
  classWrapperName?: string;
  describe?: React.ReactNode;
  disabled?: boolean;
  isRequired?: boolean;
  withoutTouch?: boolean;
} & Omit<
  DatePickerProps,
  'selectsRange' | 'onChange' | 'startDate' | 'endDate'
>;

const renderDayContents = (day: number) => {
  return <span className="flex items-center justify-center h-full">{day}</span>;
};

const CustomDateInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ value, ...props }, ref) => {
  let displayValue = value;

  if (typeof value === 'string' && value.includes(' - ')) {
    const [start, end] = value.split(' - ');
    if (start === end) {
      displayValue = start;
    }
  }

  return <input ref={ref} value={displayValue} {...props} />;
});
CustomDateInput.displayName = 'CustomDateInput';

const FormRangeDatepicker = <TFormValues extends FieldValues>({
  name,
  label,
  className,
  classWrapperName,
  isRequired,
  disabled,
  describe,
  minDate,
  maxDate,
  withoutTouch = true,
}: ControlledRangeDatepickerProps<TFormValues>) => {
  const { control } = useFormContext<TFormValues>();
  const {
    field,
    fieldState: { error, isTouched },
  } = useController({ name, control });

  const touch = withoutTouch || isTouched;

  const startDate = (Array.isArray(field.value) && field.value[0]) || undefined;
  const endDate = (Array.isArray(field.value) && field.value[1]) || undefined;

  return (
    <div
      className={twJoin(
        'flex flex-col gap-0.5',
        classWrapperName,
        disabled && 'opacity-50',
      )}
    >
      <FieldLabel name={name} label={label} isRequired={isRequired} />

      <DatePicker
        id={name}
        startDate={startDate}
        endDate={endDate}
        onChange={(update) => {
          field.onChange(update);
        }}
        onBlur={field.onBlur}
        dateFormat="MM/dd/yyyy"
        placeholderText="MM/DD/YYYY - MM/DD/YYYY"
        showPopperArrow={false}
        icon={<Datepicker className="top-1/2 -translate-y-1/2 mr-2" />}
        customInput={<CustomDateInput />}
        renderDayContents={renderDayContents}
        popperPlacement="bottom"
        popperClassName="-mt-3"
        minDate={minDate}
        maxDate={maxDate}
        className={twJoin(
          className,
          'border border-med-gray-3 text-sm h-12 w-full placeholder-med-gray-3 placeholder:font-medium outline-none focus:border-blue-600 rounded-5 py-1.5 px-3',
          error && touch && 'border-alert-negative bg-error-yellow',
        )}
        disabled={disabled}
        selectsRange
        showIcon
      />

      {error && touch && (
        <FieldError className="-mt-1.5">{error?.message as string}</FieldError>
      )}
      {!error && describe && <FieldDescribe>{describe}</FieldDescribe>}
    </div>
  );
};

export default FormRangeDatepicker;
